import React, {Component, Fragment} from 'react';
import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import Message, {getMessage} from '../common/message/Message';

import {
    Button,
    Modal,
    PrizeSpacer,
    Spacer,
    Text
} from '../../components';

import Svg from '../common/svg/Svg';

import {ProgressList} from '../progress/ProgressList';
import {StepComplete} from '../progress/StepComplete';
import {ExpandButton} from './ExpandButton';

import {PAYMENT_STATUS} from '../../constants/PaymentStatus';
import {PrizeType} from '../../constants/PrizeType';
import { getGameImage, getGameLogoClass } from '../../utils/Utils';

const PRIZE_STATUS = {
    AMBIGUOUS_PENDING: 'AMBIGUOUS_PENDING',
    AWAITING_RELEASE: 'AWAITING_RELEASE',
    COMPLETED: 'COMPLETED',
    HYPERWALLET_IN_PROGRESS: 'HYPERWALLET_IN_PROGRESS',
    NEEDS_REVERIFICATION: 'NEEDS_REVERIFICATION',
    NO_STATE: 'NO_STATE',
    NO_TAX_PROFILE: 'NO_TAX_PROFILE',
    PAYMENT_PENDING: 'PAYMENT_PENDING',
    OFFLINE_PAYMENT: 'OFFLINE_PAYMENT',
    VERIFICATION_PENDING: 'VERIFICATION_PENDING'
};

const dollarStyle = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    minimumFractionDigits: 0,
    style: 'currency'
});

/**
 * | idVerified | intrvwUrl | rlsAccept | Status |
 * |------------|-----------|-----------|--------|
 * | FALSE      | FALSE     | FALSE     | Completed interview, verification pending
 * | FALSE      | FALSE     | TRUE      | See note 😩
 * | FALSE      | TRUE      | FALSE     | Needs to do full tax interview for verification
 * | FALSE      | TRUE      | TRUE      | Possible state, if user's tax record flips from valid to invalid (expires)
 * | TRUE       | FALSE     | TRUE      | Payment pending
 * | TRUE       | TRUE      | FALSE     | User verified, needs to do release form
 * | TRUE       | TRUE      | TRUE      | Impossible/invalid state
 *
 * 😩 This is an ambiguous state that can happen in the following situations:
 * 1. Has just completed interview and is awaiting verification
 * 2. Completed initial interview, then erased profile by entering Retake flow and not finishing
 * 3. Aborted interview after accepting release form
 *
 * There is also an additional state, `offlinePayment` or `paymentHandler = 'epic'`. If this state
 * is true then the user cannot use Hyperwallet and must manually submit payment info to Epic.
 */
const getPrizeStatus = props => {
    const {
        identityTaxVerified: idVerified,
        offlinePayment,
        releaseFormAccepted: releaseAccepted,
        taxInterviewUrl: url,
        eventOfflinePaymentMarked: offlineEvent
    } = props;

    // User completed interview and verification is pending
    if (!idVerified && !url && !releaseAccepted) {
        return PRIZE_STATUS.VERIFICATION_PENDING;
    }

    // Needs to set up tax profile by doing a tax interview
    if (!idVerified && url && !releaseAccepted) {
        return PRIZE_STATUS.NO_TAX_PROFILE;
    }

    // User's tax status has changed from valid to invalid
    if (!idVerified && url && releaseAccepted) {
        return PRIZE_STATUS.NEEDS_REVERIFICATION;
    }

    // Payment pending (offline payment)
    // offline payment state is for French minors
    if (idVerified && releaseAccepted && !url && (offlinePayment || offlineEvent)) {
        return PRIZE_STATUS.OFFLINE_PAYMENT;
    }

    // Payment pending
    if (idVerified && !url && releaseAccepted) {
        return PRIZE_STATUS.PAYMENT_PENDING;
    }

    // User verified and can skip to release form
    if (idVerified && url && !releaseAccepted) {
        return PRIZE_STATUS.AWAITING_RELEASE;
    }

    // User is in ambiguous pending state (see 😩 note above)
    if (!idVerified && !url && releaseAccepted) {
        return PRIZE_STATUS.AMBIGUOUS_PENDING;
    }

    // explicit state in case the above somehow does not apply
    return PRIZE_STATUS.NO_STATE;
};

const getCurrentStep = (status, isCash) => {
    switch (status) {
        case PRIZE_STATUS.OFFLINE_PAYMENT:
            if (!isCash) {
                return 3;
            }
            return 4;
        case PRIZE_STATUS.PAYMENT_PENDING:
            return 3;
        case PRIZE_STATUS.AWAITING_RELEASE:
        case PRIZE_STATUS.NO_TAX_PROFILE:
            return 1;
        case PRIZE_STATUS.NEEDS_REVERIFICATION:
            return 1;
        case PRIZE_STATUS.VERIFICATION_PENDING:
        case PRIZE_STATUS.AMBIGUOUS_PENDING:
        case PRIZE_STATUS.NO_STATE:
            return 2;
        default:
            return 1;
    }
};

export class Prize extends Component {
    static displayName = 'Prize';

    static propTypes = {
        dateOfEvent: PropTypes.string,
        displayName: PropTypes.object,
        eventName: PropTypes.string,
        gameId: PropTypes.string,
        prizeId: PropTypes.string,
        identityTaxVerified: PropTypes.bool,
        messages: PropTypes.object,
        offlinePayment: PropTypes.bool,
        paymentAmount: PropTypes.number,
        paymentRestricted: PropTypes.string,
        eventOfflinePaymentMarked: PropTypes.bool,
        paymentStatus: PropTypes.string,
        taxInterviewUrl: PropTypes.string,
        winningAmount: PropTypes.number,
        withholdingAmount: PropTypes.number,
        history: PropTypes.object,
        prizeType: PropTypes.string,
        isValidationTaxProfile: PropTypes.bool,
        markedOfflinePay: PropTypes.bool,
        isPrizeGated: PropTypes.bool
    };

    state = {
        showModal: false,
        isExpanded: false
    };

    closeModal = () => {
        this.setState({showModal: false});
    }

    onConfirm = () => {
        const { taxInterviewUrl, eventName, history, prizeId } = this.props;
        history.push('interview', {taxInterviewUrl, eventName, isRetake: false, prizeId});
    }

    showModal = () => {
        this.setState({showModal: true});
    }

    handleToggle = () => {
        this.setState({isExpanded: !this.state.isExpanded});
    }

    render() {
        const {
            dateOfEvent,
            displayName,
            eventName,
            prizeId,
            gameId,
            identityTaxVerified,
            messages,
            offlinePayment,
            eventOfflinePaymentMarked = false,
            paymentAmount,
            paymentRestricted,
            paymentStatus,
            taxInterviewUrl,
            winningAmount,
            withholdingAmount,
            prizeType,
            isValidationTaxProfile,
            markedOfflinePay = false,
            isPrizeGated
        } = this.props;

        let status = '';
        let currentStep = 0;
        let isCompleted = false;

        const isCash = prizeType === PrizeType.CASH;
        const isPrizeOffline = eventOfflinePaymentMarked || offlinePayment;
        // if payment has been sent to Hyperwallet, this supercedes the Tax Identity status messages
        if (paymentStatus === PAYMENT_STATUS.COMPLETED) {
            status = PRIZE_STATUS.COMPLETE;
            isCompleted = true;
        } else if (paymentStatus === PAYMENT_STATUS.IN_PROGRESS && !isPrizeOffline) {
            status = PRIZE_STATUS.HYPERWALLET_IN_PROGRESS;
            currentStep = 4;
            if (!isCash) {
                currentStep = 3;
            }
        // if Hyperwallet status is `inProgress`, then use Tax Identity status messages
        } else if (markedOfflinePay) {
            status = PRIZE_STATUS.COMPLETED;
            isCompleted = true;
        } else {
            status = getPrizeStatus(this.props);
            currentStep = getCurrentStep(status, isCash);
        }

        const dateString = moment(dateOfEvent).format('MMM D, YYYY');
        const withheld = (paymentAmount === 0 && withholdingAmount === 0) ? 'Pending' : dollarStyle.format(withholdingAmount);
        const netPayout = (paymentAmount === 0) ? 'Pending' : dollarStyle.format(paymentAmount);

        const PaymentWithRestrict = () => (<Spacer paddingTop={8}>
            <Message as="p" code="epic.esport.hyperwallet.restricted.help.title" args={[displayName]} />
            <Message as="p" code="epic.esport.hyperwallet.restricted.help.message1" />
            <Message as="p" code="epic.esport.hyperwallet.restricted.help.message2" />
        </Spacer>);

        const progressContent = isCompleted ? (
            <StepComplete>
                <Message fragment code="epic.esport.stepComplete.body" />
            </StepComplete>
        ) : (
            <ProgressList
                currentStep={currentStep}
                isCash={isCash}
                hasUrl={taxInterviewUrl ? true : null}
                idVerified={identityTaxVerified}
                offlinePayment={isPrizeOffline}
                onButtonClick={this.showModal}
                taxInterviewUrl={taxInterviewUrl}
                messages={messages}
                eventName={eventName}
                markedOfflinePay={markedOfflinePay}
                isValidationTaxProfile={isValidationTaxProfile}
                isPrizeGated={isPrizeGated}
                gameId={gameId}
                prizeId={prizeId}
            />
        );

        return (
            <PrizeSpacer
                bottom={24}
                className={cx(
                    'prize__root',
                    this.state.isExpanded && 'prize__root--expand'
                )}
                padding={null}
                paddingLeft={40}
                paddingRight={40}
                paddingTop={40}
            >
                <Spacer className="prize__game">
                    <Svg
                        image={getGameImage(gameId)}
                        className={getGameLogoClass(gameId)}
                    />
                </Spacer>

                <Spacer className="prize__tournament" bottom={8}>
                    <div className="prize__value prize__event-name">{eventName}</div>
                    <div className="prize__date">{dateString}</div>
                </Spacer>


                <Spacer className="prize__field" right={8} bottom={8}>
                    <Message as="div" code={isCash ? 'epic.esport.label.prize.payout_type.cash' : 'epic.esport.label.prize.payout_type.pg'} className="prize__label" />
                </Spacer>


                <Spacer className="prize__field" right={8} bottom={8}>
                    <Message as="div" code="epic.esport.label.prize.pre_tax" className="prize__label" />
                    <div className="prize__value">{dollarStyle.format(winningAmount)}</div>
                </Spacer>

                <Spacer className="prize__field" right={8} bottom={8}>
                    <Message as="div" code="epic.esport.label.prize.tax_withheld" className="prize__label" />
                    <div className="prize__value">{withheld}</div>
                </Spacer>

                <Spacer className="prize__field" bottom={8}>
                    <Message as="div" code={isCash ? 'epic.esport.label.prize.net_payout_amount' : 'epic.esport.label.prize.net_payout_value'} className="prize__label" />
                    <div className="prize__value">{netPayout}</div>
                </Spacer>

                <ExpandButton
                    onClick={this.handleToggle}
                    expanded={this.state.isExpanded}
                    className="expandbtn__prize"
                    iconProps={{className: 'expandbtn__prizeicon'}}
                />

                {
                    this.state.isExpanded && (
                        <Fragment>
                            {paymentRestricted ? <PaymentWithRestrict /> : progressContent}
                        </Fragment>
                    )
                }

                <Modal
                    onClose={this.closeModal}
                    show={this.state.showModal}
                >
                    <div className="tax-profile-confirm">
                        <Text size={18}>
                            {getMessage(messages, 'epic.esport.interview.warning1')}
                        </Text>

                        <Text size={18} top={8}>
                            {getMessage(messages, 'epic.esport.interview.warning2')}
                        </Text>

                        <div className="tp-modal-btns">
                            <Button
                                variant="alt"
                                onClick={this.closeModal}
                                className="tp-btn"
                            >
                                {getMessage(messages, 'epic.esport.profile.retake.no')}
                            </Button>
                            <Button
                                className="tp-btn pz-btn"
                                onClick={this.onConfirm}
                            >
                                {getMessage(messages, 'epic.esport.profile.continue')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            </PrizeSpacer>
        );
    }
}
